import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { cn } from '@/lib/utils';

interface ErrorStateProps {
  readonly error: SerializedError | FetchBaseQueryError;
  readonly entityType?: string;
  readonly className?: string;
}

const getErrorMessage = (
  error: SerializedError | FetchBaseQueryError,
): string => {
  // Handle FetchBaseQueryError
  if ('status' in error) {
    if (error.status === 'FETCH_ERROR') {
      return 'Network error';
    }
    if (error.status === 'PARSING_ERROR') {
      return 'Data error';
    }
    if (error.status === 'TIMEOUT_ERROR') {
      return 'Timed out';
    }
    if (typeof error.status === 'number') {
      if (error.status === 404) {
        return 'Not found';
      }
      if (error.status === 403) {
        return 'Access denied';
      }
      if (error.status === 401) {
        return 'Unauthorized';
      }
      if (error.status >= 500) {
        return 'Server error';
      }
      return `Error ${error.status}`;
    }
  }

  // Handle SerializedError
  if ('message' in error && error.message) {
    return error.message;
  }

  return 'Unknown error';
};

export const ErrorState = ({
  error,
  entityType,
  className,
}: ErrorStateProps) => {
  const message = getErrorMessage(error);

  return (
    <div className={cn('bg-red-50/50 p-2', className)}>
      <div className="flex items-start gap-2">
        <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-red-900">
            Failed to load {entityType || 'entity'}
          </p>
          <p className="text-[10px] leading-relaxed text-red-600">{message}</p>
        </div>
      </div>
    </div>
  );
};
